import { forwardRef, useEffect, useState } from "react";
import { Box, Flex, Image, Text } from "rebass/styled-components";
import { useRecoilValue, useSetRecoilState } from "recoil";
import styled from "styled-components";

import { Thumbnails } from "../Thumbnails";
import EmbeddablePlayer from "../../../EmbeddablePlayer";
import { WatchLimitBlocker } from "../WatchLimitBlocker";
import {
  ActivePlaylistClipState,
  NextUpPlaylistState,
  VisiblePlaylistClipsState,
  ShowWatchLimitBlockerState,
  BlockedPlayerNameState,
  BlockedClipState,
} from "MVVM/State/embeddablePlaylist";
import { GenericEmbeddablePlaylistViewModel } from "./ViewModel";
import { LoadingSkeleton } from "MVVM/Components/Skeloton";
import { useWatchLimit } from "MVVM/Hooks/useWatchLimit";
import { GenericNoClips } from "./Components/GenericNoClips";

export const GenericEmbeddablePlaylist = forwardRef<
  HTMLDivElement,
  {
    platform: string;
    playlist: string;
    handleMouseEnter: () => void;
  }
>(({ platform, playlist, handleMouseEnter }, ref) => {
  const activePlaylistClip = useRecoilValue(ActivePlaylistClipState);
  const visiblePlaylistClips = useRecoilValue(VisiblePlaylistClipsState);
  const setIsNextUpPlaylist = useSetRecoilState(NextUpPlaylistState);
  const showWatchLimitBlocker = useRecoilValue(ShowWatchLimitBlockerState);
  const blockedPlayerName = useRecoilValue(BlockedPlayerNameState);
  const blockedClip = useRecoilValue(BlockedClipState);
  const { handleCloseBlocker } = useWatchLimit();

  const searchParams = new URLSearchParams(window.location.search);
  const showCarousel = searchParams.get("showCarousel") !== "false";

  const { isLoading } = GenericEmbeddablePlaylistViewModel({
    playlist,
    platform,
  });

  useEffect(() => {
    setIsNextUpPlaylist(true);
  }, [setIsNextUpPlaylist, playlist]);

  const hasClips = visiblePlaylistClips && visiblePlaylistClips?.length > 0;

  if (isLoading) return null;

  if (!hasClips) return <GenericNoClips />;

  return (
    <>
      <StyledContainer onMouseEnter={handleMouseEnter}>
        <StyledGradientLayer className="gradient-layer">
          <StyledHeaderContainer>
            <Flex style={{ gap: "8px", alignItems: "center" }}>
              <AllstarLogo
                src={`${process.env.REACT_APP_S3_CLIP_BUCKET}/static/web/Logo.svg`}
              />
              <Text variant="text.labelSmall" fontWeight={700} color="white">
                {activePlaylistClip?.title || "Gaming Highlights"}
              </Text>
            </Flex>
            <Flex alignItems="center" style={{ gap: "4px" }}>
              <StyledPoweredByText variant="text.labelXXSmall">
                Powered by
              </StyledPoweredByText>
              <Image
                width={48}
                height={10}
                src={`${process.env.REACT_APP_S3_CLIP_BUCKET}/static/web/iframe/allstargg.png`}
                onClick={() => {
                  const ajs_aid = window.rudderanalytics.getAnonymousId();
                  return window.open(
                    `https://allstar.gg?utm_source=${platform}&ajs_aid=${ajs_aid}`,
                    "_blank",
                  );
                }}
                style={{ cursor: "pointer" }}
              />
            </Flex>
          </StyledHeaderContainer>
          <StyledPlayerColumn $showCarousel={showCarousel}>
            <StyledPlayerRow>
              <FixedPlayerWrapper ref={ref}>
                {activePlaylistClip ? (
                  <EmbeddablePlayer
                    className="player-component"
                    userRole="viewer"
                    onPlayEndVariant="nextUpPlaylist"
                    hideNextUpThumbnail={true}
                  />
                ) : (
                  <PlayerLoadingSkeleton>
                    <LoadingSkeleton width="100%" height="100%" />
                  </PlayerLoadingSkeleton>
                )}
                {showWatchLimitBlocker && (
                  <WatchLimitBlocker
                    onClose={handleCloseBlocker}
                    playerName={blockedPlayerName}
                    clickedClip={blockedClip}
                    primaryColor="#4DBD94"
                    playlistId={playlist}
                  />
                )}
              </FixedPlayerWrapper>
              {showCarousel && <RightSpacer />}
            </StyledPlayerRow>
          </StyledPlayerColumn>
          {showCarousel && (
            <ThumbnailColumn>
              <Thumbnails
                clipsToDisplay={visiblePlaylistClips}
                platform={platform}
              />
            </ThumbnailColumn>
          )}
        </StyledGradientLayer>
      </StyledContainer>
    </>
  );
});

const StyledContainer = styled(Box)`
  background: linear-gradient(180deg, #0f1419 0%, #1a1f26 100%);
  height: 100%;
  overflow: hidden;
  position: relative;
  width: 100%;
`;

const StyledGradientLayer = styled(Flex)`
  flex-direction: column;
  gap: 8px;
  height: 100%;
  padding: 10px;
  position: relative;
  width: 100%;

  @media (max-width: 425px) {
    padding: 6px;
    .fixed-width-container {
      width: 107px !important;
      height: 60px !important;
    }
  }

  @media (max-width: 380px) {
    .fixed-width-container {
      width: 96px !important;
      height: 54px !important;
    }
  }
`;

const StyledHeaderContainer = styled(Flex)`
  align-items: center;
  backdrop-filter: blur(8px);
  background: rgba(0, 0, 0, 0.6);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  flex: 0 0 auto;
  justify-content: space-between;
  padding: 8px 12px;
  z-index: 10;
`;

const AllstarLogo = styled(Image)`
  border-radius: 4px;
  height: 20px;
  width: 20px;
`;

const StyledPoweredByText = styled(Text)`
  color: rgba(255, 255, 255, 0.6);
  font-size: 10px;
`;

const StyledPlayerRow = styled(Flex)`
  flex: 1;
  gap: 12px;
  min-height: 0;
  min-width: 0;
`;

const FixedPlayerWrapper = styled(Box)`
  border-radius: 8px;
  flex: 1;
  min-height: 0;
  min-width: 0;
  position: relative;
  width: 100%;

  .video-js {
    background: transparent;
    height: 100% !important;
    width: 100% !important;
  }

  .vjs-tech {
    height: 100% !important;
    width: 100% !important;
  }
`;

const PlayerLoadingSkeleton = styled(Box)`
  height: 100%;
  width: 100%;
`;

const StyledPlayerColumn = styled(Box)<{ $showCarousel: boolean }>`
  align-items: stretch;
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-height: fit-content;
  min-width: 0;
`;

const ThumbnailColumn = styled(Box)`
  display: flex;
  flex-direction: column;
  min-height: 0;

  @media (max-width: 502px) {
    flex: 1 1 0%;
  }

  @media (max-width: 360px) and (max-height: 244px) {
    display: none;
  }
`;

const RightSpacer = styled(Box)`
  flex: 0 0 10px;
  min-width: 10px;

  @media (max-width: 515px) {
    flex: 0 0 50px;
    min-width: 50px;
  }

  @media (max-width: 425px) {
    flex: 0 0 10px;
    min-width: 10px;
  }
`;
